/**
 * Kill-switch for **range-mutation workbook targeting**.
 *
 * `f2_docs/excel/excel-tools.md` is the source of truth and documents that
 * the range-mutation intents (`ApplyFormatting`, `DeleteRange`, `InsertRange`,
 * `ClearRange`, `SortRange`, `MoveRange`, `ResizeExcelElements`) honour a
 * `RangeReferenceAddress.workbookName` routing override — so Finster targets
 * a run's origin workbook in the *background* instead of activating it (which
 * yanks the user's Excel focus around during concurrent multi-workbook runs).
 *
 * This flag is a **default-ON kill-switch**, NOT a verify-before-trust gate.
 * We trust the documented capability and ship it on. If a real host
 * regression is ever observed (a mutation lands on the wrong / active
 * workbook), flip this OFF to fall back to the legacy behaviour:
 * `applyWorkbookModificationsViaIntents` re-activates the pinned workbook
 * before the burst and sends the mutation intents WITHOUT `workbookName`,
 * exactly as before this capability landed.
 *
 * Resolution order (first hit wins):
 *   1. An explicit in-process override (`setRangeMutationWorkbookNameSupportOverride`) —
 *      used by tests and by an ops "flip it now, no rebuild" path.
 *   2. A `localStorage` flag (`finster.excel.rangeMutationWorkbookName` set to
 *      `"off"` / `"false"` / `"0"`) — a no-rebuild kill-switch for support.
 *   3. Default: ON.
 *
 * Mirrors the injectable-reader shape of `shared/office/officeJsBypass.ts`.
 */

const LOCAL_STORAGE_KEY = "finster.excel.rangeMutationWorkbookName";

let override: boolean | null = null;

/**
 * Force the capability on/off in-process, or pass `null` to clear the
 * override and fall back to the localStorage / default resolution. Primarily
 * for tests and an ops kill-switch.
 */
export function setRangeMutationWorkbookNameSupportOverride(
  value: boolean | null,
): void {
  override = value;
}

function readLocalStorageOverride(): boolean | null {
  try {
    // `localStorage` is unavailable in some host contexts (SSR, sandboxed
    // iframes). Guard defensively — a read failure just means "no override".
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw === null) return null;
    const normalized = raw.trim().toLowerCase();
    if (normalized === "off" || normalized === "false" || normalized === "0") {
      return false;
    }
    if (normalized === "on" || normalized === "true" || normalized === "1") {
      return true;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * True when Finster should target range-mutation intents at the run's pinned
 * workbook via `workbookName` (the background path). Defaults to `true`.
 */
export function supportsRangeMutationWorkbookName(): boolean {
  if (override !== null) return override;
  const stored = readLocalStorageOverride();
  if (stored !== null) return stored;
  return true;
}
